import React, { useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FaArrowUp, FaArrowDown, FaArrowRight } from 'react-icons/fa';
import { COLORS } from '../../../../../../../constants/radar';
import { CommentsField } from '../ReviewerCompetencyList/CommentsField';
import ConfirmationModal from '../../../../../../common/ConfirmationModal';
import ReviewerFeedbackActionList from './ReviewerFeedbackActionList';
import { MIN_COMMENTS_LENGTH } from '../../../../../../../constants/assignmentConstants';

const ActionsStep = ({
  activeStep,
  assignment,
  myReviewer,
  employeeId,
  isDirty,
  onSaveActions,
  submittingActions = false,
  confirmDialogOpen,
  setConfirmDialogOpen,
  handleSubmitWithConfirmation,
  areFeedbackActionsValid
}) => {
  const handleStartActionsChange = useCallback(async (comments) => {
    try {
      await onSaveActions('start', comments);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }, [onSaveActions]);

  const handleStopActionsChange = useCallback(async (comments) => {
    try {
      await onSaveActions('stop', comments);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }, [onSaveActions]);

  const handleContinueActionsChange = useCallback(async (comments) => {
    try {
      await onSaveActions('continue', comments);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }, [onSaveActions]);

  // Find the current reviewer from assignment.reviewers (similar to ReviewerCompetencyList pattern)
  const currentReviewer = assignment?.reviewers?.find(reviewer => 
    reviewer.employee.id === employeeId && reviewer.id === myReviewer?.id
  ) || myReviewer;

  // Check if actions are already submitted
  const allReviewersCompleted = assignment.reviewsCompletedAt;
  // ADR-014 §7: a snapshot-closed session freezes this assignment — inputs and
  // submit are read-only (the API also rejects the writes with 409).
  const sessionLocked = !!assignment?.isLocked;
  const areActionsSubmitted =
    currentReviewer?.feedbackAttachedAt || allReviewersCompleted || sessionLocked;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ 
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: COLORS.PRIMARY,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
          fontSize: '16px',
          fontWeight: 500
        }}>
          {activeStep}
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.PRIMARY }}>
          Provide Feedback Actions
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: sessionLocked ? 2 : 4 }}>
        Provide actionable feedback for <strong>{assignment?.employee?.fullName}</strong> based on your assessment. 
        Fill in the relevant sections below to help guide their professional development.
      </Typography>

      {/* ADR-014: the session was closed before this step was finished. */}
      {sessionLocked && (
        <Box
          sx={{
            mb: 4,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#F1F5F9',
            border: '1px solid #94A3B8'
          }}
        >
          <Typography variant="body2" sx={{ color: '#475569' }}>
            <strong>Session closed.</strong> This feedback session has been closed, so these
            actions are preserved as they were and can no longer be edited or submitted.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mb: 4 }}>
        {/* START Actions */}
        <Box sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 3,
          borderLeftWidth: 4,
          borderLeftColor: '#10B981'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FaArrowUp size={16} color="#10B981" style={{ marginRight: 12 }} />
            <Typography variant="subtitle1" sx={{ color: '#10B981', fontWeight: 500 }}>
              START
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            What should {assignment?.employee?.fullName} start doing to improve their performance?
          </Typography>
          
          {/* Show previous reviewers' START actions */}
          <ReviewerFeedbackActionList
            actionType="start"
            assignment={assignment}
            employeeId={employeeId}
            currentReviewerId={currentReviewer?.id}
          />
          
          <CommentsField
            initialComments={currentReviewer?.feedbackOnDemand?.startComments || ""}
            onCommentChange={handleStartActionsChange}
            disabled={areActionsSubmitted}
            skillId="start-actions"
            reviewerId={currentReviewer?.id}
            required={true}
            minContentsLength={MIN_COMMENTS_LENGTH}
            fieldId={`${currentReviewer?.id}-start-actions`}
            placeholder="Suggest new actions tied to development or growth opportunities."
          />
        </Box>

        {/* STOP Actions */}
        <Box sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 3,
          borderLeftWidth: 4,
          borderLeftColor: '#EF4444'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FaArrowDown size={16} color="#EF4444" style={{ marginRight: 12 }} />
            <Typography variant="subtitle1" sx={{ color: '#EF4444', fontWeight: 500 }}>
              STOP
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            What should {assignment?.employee?.fullName} stop doing or reduce to improve their effectiveness?
          </Typography>
          
          {/* Show previous reviewers' STOP actions */}
          <ReviewerFeedbackActionList
            actionType="stop"
            assignment={assignment}
            employeeId={employeeId}
            currentReviewerId={currentReviewer?.id}
          />
          
          <CommentsField
            initialComments={currentReviewer?.feedbackOnDemand?.stopComments || ""}
            onCommentChange={handleStopActionsChange}
            disabled={areActionsSubmitted}
            skillId="stop-actions"
            reviewerId={currentReviewer?.id}
            required={true}
            minContentsLength={MIN_COMMENTS_LENGTH}
            fieldId={`${currentReviewer?.id}-stop-actions`}
            placeholder="Focus on behavior change that affects team performance or individual growth."
          />
        </Box>

        {/* CONTINUE Actions */}
        <Box sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 3,
          borderLeftWidth: 4,
          borderLeftColor: COLORS.PRIMARY
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FaArrowRight size={16} color={COLORS.PRIMARY} style={{ marginRight: 12 }} />
            <Typography variant="subtitle1" sx={{ color: COLORS.PRIMARY, fontWeight: 500 }}>
              CONTINUE
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            What is {assignment?.employee?.fullName} currently doing well and should continue or expand on?
          </Typography>
          
          {/* Show previous reviewers' CONTINUE actions */}
          <ReviewerFeedbackActionList
            actionType="continue"
            assignment={assignment}
            employeeId={employeeId}
            currentReviewerId={currentReviewer?.id}
          />
          
          <CommentsField
            initialComments={currentReviewer?.feedbackOnDemand?.continueComments || ""}
            onCommentChange={handleContinueActionsChange}
            disabled={areActionsSubmitted}
            skillId="continue-actions"
            reviewerId={currentReviewer?.id}
            required={true}
            minContentsLength={MIN_COMMENTS_LENGTH}
            fieldId={`${currentReviewer?.id}-continue-actions`}
            placeholder="Focus on strengths worth reinforcing."
          />
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        mt: 4,
        pt: 3,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Box
          onClick={() => {
            if (!areActionsSubmitted && !submittingActions && !isDirty && areFeedbackActionsValid()) {
              setConfirmDialogOpen(true);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'white',
            backgroundColor: COLORS.PRIMARY,
            cursor: !areActionsSubmitted && !submittingActions && !isDirty && areFeedbackActionsValid() ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 16px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            opacity: !areActionsSubmitted && !submittingActions && !isDirty && areFeedbackActionsValid() ? 1 : 0.5,
            '&:hover': {
              opacity: !areActionsSubmitted && !submittingActions && !isDirty && areFeedbackActionsValid() ? 0.9 : 0.5
            }
          }}
        >
          {areActionsSubmitted 
            ? 'Actions Already Submitted' 
            : submittingActions 
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Submitting Actions
                  <CircularProgress 
                    size={16} 
                    thickness={4} 
                    sx={{ color: 'white' }} 
                  />
                </Box>
              ) 
              : 'Submit Actions'
          }
        </Box>
      </Box>

      <ConfirmationModal
        isOpen={confirmDialogOpen && !isDirty && areFeedbackActionsValid()}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleSubmitWithConfirmation}
        title="Confirm Actions Submission"
        message="Are you sure you want to submit these feedback actions? This action cannot be undone."
        actionButtonText="Submit Actions"
      />

    </Box>
  );
};

export default ActionsStep; 